import type { FieldValues, Resolver } from 'react-hook-form';

export type LazyResolver<TFieldValues extends FieldValues, TContext, TTransformedValues> = Resolver<
  TFieldValues,
  TContext,
  TTransformedValues
> & {
  /** Start fetching the resolver's code (Zod and the schema) without validating anything. */
  preload: () => void;
};

/**
 * A react-hook-form resolver whose code is fetched the first time a value has to be validated.
 *
 * The usual `zodResolver(schema)` makes the page import Zod (25 kB gzip) just to *render* a form, although nothing
 * is checked until a field has been touched or the form is sent. This one is a few lines: it imports the real
 * resolver (`load`) on first use, keeps it, and hands every call over. Call `preload` when the visitor first
 * touches the form (`<form onFocusCapture={resolver.preload}>`), so the code is usually there before the first
 * field loses focus; if it is not, validation simply waits for it. A failed fetch (offline, a chunk gone after a
 * deploy) is retried at the next validation.
 */
export function lazyResolver<
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  load: () => Promise<Resolver<TFieldValues, TContext, TTransformedValues>>,
): LazyResolver<TFieldValues, TContext, TTransformedValues> {
  let loading: Promise<Resolver<TFieldValues, TContext, TTransformedValues>> | undefined;
  const real = (): Promise<Resolver<TFieldValues, TContext, TTransformedValues>> => {
    loading ??= load().catch((error: unknown) => {
      loading = undefined;
      throw error;
    });
    return loading;
  };

  const resolver: Resolver<TFieldValues, TContext, TTransformedValues> = async (
    values,
    context,
    options,
  ) => {
    const resolve = await real();
    return resolve(values, context, options);
  };
  return Object.assign(resolver, {
    preload: (): void => {
      void real().catch(() => undefined);
    },
  });
}
